import { useEffect, useState } from 'react';
import { getAdobeVisitorValues } from '@tv2/lib-offspring/libs/adobe/adobeVisitor';
import { adobeTracking } from '@tv2/lib-offspring/libs/track';
import { useQuery } from '@tv2/lib-graphql/client';
import { useDUID } from '@tv2/lib-offspring/hooks/useDUID';
import { PromptsDocument, PromptsQuery } from './__generated__/prompts';
import { PromptMutationDocument } from './__generated__/promptAction';

export type PromptData = NonNullable<PromptsQuery['prompts']>[number];
export type Prompt = {
  data: PromptData;
  action: (actionPayload: string) => void;
  closePrompt: () => void;
  track: (event: string, data: Record<string, string>) => void;
};

export function usePrompt(): Prompt | null {
  const adobeValues = typeof window === 'undefined' ? undefined : getAdobeVisitorValues();
  const isGoogleBot =
    typeof window === 'undefined' ? false : navigator.userAgent.toLowerCase().includes('googlebot');

  const [mutationAction, setMutationAction] = useState<null | string>(null);
  const [closedPromptsCount, setClosedPromptsCount] = useState<number>(0);
  const adobeHeaders = adobeValues
    ? {
        'client-platform-adobe-visitor': JSON.stringify(adobeValues),
      }
    : undefined;

  const duid = useDUID();
  const duidHeaders = duid
    ? {
        'client-platform-duid': duid,
      }
    : undefined;

  const defaultHeaders = {
    'Client-Platform-compatibility-Version': '2',
    'client-platform-timezone': new Date().getTimezoneOffset().toString(),
  };

  const { data } = useQuery({
    headers: { ...adobeHeaders, ...defaultHeaders, ...duidHeaders },
    requestPolicy: 'network-only',
    query: PromptsDocument,
    pause: isGoogleBot,
  });

  useEffect(() => {
    if (!data || !data.prompts) {
      return undefined;
    }

    if (data.prompts.length === closedPromptsCount) {
      return undefined;
    }

    const prompt = data.prompts[closedPromptsCount];
    if (prompt) {
      adobeTracking({
        event: prompt.impressionTracking.identifier,
        data: prompt.impressionTracking.details,
      } as any);
    }
  }, [data?.prompts, closedPromptsCount, data]);

  useQuery({
    headers: adobeValues
      ? {
          'client-platform-adobe-visitor': JSON.stringify(adobeValues),
        }
      : undefined,
    variables: {
      mutationPayload: mutationAction ?? '',
    },
    query: PromptMutationDocument,
    pause: mutationAction === null,
  });

  if (!data || !data.prompts) {
    return null;
  }

  if (data.prompts.length === closedPromptsCount) {
    return null;
  }

  return {
    data: data.prompts[closedPromptsCount],
    closePrompt: () => {
      setClosedPromptsCount(x => x + 1);
    },
    action: (actionPayload: string) => {
      setMutationAction(actionPayload);
    },
    track: (event: string, _data: Record<string, string>) => {
      adobeTracking({
        event,
        data: _data,
      } as any);
    },
  };
}
